import type React from 'react';

import { Link, useNavigate } from 'react-router';

import { Urls } from '../types/ExtendEnums';
import type {
  BehandlingsHovedGruppe,
  BehandlingsUnderGruppe,
  Behandling,
} from '../types/Helsenorge.VelgBehandlingssted.Libraries.EntitiesEntities';
import type { HNVelgBehandlingsstedFrontend } from '../types/Resources';

import ExpanderList from '@helsenorge/designsystem-react/components/ExpanderList';
import HighlightPanel from '@helsenorge/designsystem-react/components/HighlightPanel';
import LazyIcon from '@helsenorge/designsystem-react/components/LazyIcon';
import Title from '@helsenorge/designsystem-react/components/Title';
import designsystemtypography from '@helsenorge/designsystem-react/scss/typography.module.scss';

import type { Suggestion } from '@helsenorge/autosuggest/components/autosuggest';
import PageHeader from '@helsenorge/core-framework/page-header';
import PageLoader from '@helsenorge/core-framework/page-loader';
import { getSidetittelId } from '@helsenorge/framework-utils/hn-page';

import { useResources } from '../resources';
import AutosuggestComponent from '../shared/autosuggest';
import {
  useGetBehandlingerQuery,
  useGetSokBehandlingerQuery,
  useGetSokBehandlingerSuggestionsQuery,
} from '../store/api/velgBehandlingsstedApi';
import { behandlingSvgIkonMapper } from '../utils/behandling-ikon-mapper';
import { HN_VELG_BEHANDLINGSSTED_FRONTEND } from '../utils/resources-project';
import './styles.scss';

const Behandlinger: React.FunctionComponent = () => {
  const navigate = useNavigate();

  const resources = useResources<HNVelgBehandlingsstedFrontend>(HN_VELG_BEHANDLINGSSTED_FRONTEND);

  const { data: behandlingerData, isLoading: loadingBehandlinger, isError: errorBehandlinger } = useGetBehandlingerQuery();
  const { isLoading: loadingSokBehandlinger } = useGetSokBehandlingerQuery();

  const hovedgrupper = behandlingerData?.behandlingsGrupper;

  const { data: allSuggestions = [] } = useGetSokBehandlingerSuggestionsQuery();

  const handleSuggestionSelected = (suggestion: Suggestion): void => {
    navigate(`${Urls.VentetiderForBehandling}?bid=${suggestion.value}`);
  };

  return (
    resources && (
      <PageLoader
        resources={resources}
        loading={loadingBehandlinger || loadingSokBehandlinger}
        error={errorBehandlinger ? resources.tekniskFeilBehandlinger : undefined}
        labelId={getSidetittelId()}
      >
        <PageHeader title={resources.behandlingerTittel} hjelpetekstId="allebehandlinger" />

        <HighlightPanel color={'neutral'}>
          <AutosuggestComponent
            label={resources.behandlingerSokLabel}
            containerLabel={resources.behandlingerTittel}
            allSuggestions={allSuggestions}
            handleSuggestionSelected={handleSuggestionSelected}
          />
        </HighlightPanel>

        {hovedgrupper?.map((hovedgruppe: BehandlingsHovedGruppe | null) => {
          return (
            hovedgruppe && (
              <section className="vbs-behandlinger__hovedgruppe" key={hovedgruppe.navn}>
                <Title htmlMarkup="h2" appearance={'title2'} margin={1}>
                  {hovedgruppe.navn}
                </Title>

                <ExpanderList color="neutral">
                  {hovedgruppe.behandlingsUnderGrupper?.map((undergruppe: BehandlingsUnderGruppe) => {
                    return (
                      <ExpanderList.Expander
                        testId="undergruppe-button"
                        key={undergruppe.navn}
                        icon={<LazyIcon iconName={behandlingSvgIkonMapper(undergruppe.kode)} />}
                        title={undergruppe.navn ? undergruppe.navn : ''}
                        color="white"
                      >
                        <ul
                          className={`vbs-behandlinger__undergruppe ${designsystemtypography['anchorlink-wrapper']}`}
                          data-testid="undergruppe"
                        >
                          {undergruppe.behandlinger?.map((behandling: Behandling) => {
                            return (
                              <li key={behandling.behandlingsId}>
                                <Link to={`${Urls.VentetiderForBehandling}?bid=${behandling.behandlingsId}`}>{behandling.navn}</Link>
                              </li>
                            );
                          })}
                        </ul>
                      </ExpanderList.Expander>
                    );
                  })}
                </ExpanderList>
              </section>
            )
          );
        })}
      </PageLoader>
    )
  );
};

export default Behandlinger;
